import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Grid from '@config/Grid/Grid'
import Alert from '@/shared/components/Alert/Alert'
import Button from '@/shared/components/Button/Button'
import IconButton from '@/shared/components/IconButton/IconButton'
import Tile from '@/shared/components/Tile/Tile'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { useWebsiteSettingsStore } from '@/shared/store/useWebsiteSettingsStore'
import EditorCanvas from '@/features/website-builder/components/EditorCanvas/EditorCanvas'
import PagePreview from '@/features/website-builder/components/PagePreview/PagePreview'
import PropertiesPanel from '@/features/website-builder/components/PropertiesPanel/PropertiesPanel'
import TokenSelect from '@/features/website-builder/components/PropertiesPanel/TokenSelect'
import SiteSettingsForm from '@/features/website-builder/components/SiteSettingsForm/SiteSettingsForm'
import {
  toSiteSettings,
  useRestaurantSettingsStore,
  siteSnapshotIsStale
} from '@/features/restaurant-settings'
import {
  useGoogleLocationQuery,
  restaurantMapUrl
} from '@/features/google-location'
import { useAutosave } from '@/features/website-builder/hooks/useAutosave'
import { useDialogOpen } from '@/features/website-builder/hooks/useDialogOpen'
import { useGoogleFonts } from '@/features/website-builder/hooks/useGoogleFonts'
import { useLocalAssetUrls } from '@/features/website-builder/hooks/useLocalAssetUrls'
import { useRestaurantMenu } from '@/features/website-builder/hooks/useRestaurantMenu'
import { hasLocalAssets } from '@/features/website-builder/schema/publish'
import { resolveFonts } from '@/features/website-builder/render/fonts'
import { useEditorShortcuts } from '@/features/website-builder/hooks/useEditorShortcuts'
import { useWebsiteQuery } from '@/features/website-builder/hooks/useWebsiteQuery'
import {
  EditorMode,
  useWebsiteStore
} from '@/features/website-builder/store/useWebsiteStore'
import {
  buildDocument,
  extraOfRowId,
  normalizeSiteSettings,
  sectionOfRowId,
  templateOf,
  templateTheme,
  siteSettingsComplete
} from '@/features/website-builder/templates'
import { sectionCardId } from '@/features/website-builder/components/SectionsEditor/SectionsEditor'
import { extraCardId } from '@/features/website-builder/components/SectionsEditor/ExtraCard'
import type { SiteSettings } from '@/features/website-builder'
import type { Theme } from '@/features/website-builder/render/theme'
/*
  The published site's own stylesheet, loaded so the canvas shows the real
  thing. Everything in it is prefixed `mp-` and nothing in this app uses that
  prefix, so the only rules that reach outside the canvas are a box-sizing reset
  and a zero body margin, both of which the app already sets.
*/
import '@/features/website-builder/render/site.scss'
import styles from './WebsiteBuilder.module.scss'

type Device = 'desktop' | 'mobile'

interface WebsiteBuilderProps {
  mode: EditorMode
}

/**
 * Two ways to make the page, as two sidebar entries.
 *
 * "Prosty szablon" (/website/template): a form on the left, the page it
 * produces on the right, redrawn on every keystroke. Nothing to drag, nothing
 * to learn.
 *
 * "Kreator" (/website/builder): the block builder. The same document, edited
 * freely.
 *
 * Both work on ONE document, and the template is rigid: its preview is always
 * exactly what the form would generate, never what the builder has since done
 * to the page. Saving from the template replaces the page with that output.
 * The store's `customized` flag exists so that replacement asks first when it
 * would throw away work done in the builder.
 */
function WebsiteBuilder({ mode }: WebsiteBuilderProps) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const [device, setDevice] = useState<Device>('desktop')
  const [publishedNotice, setPublishedNotice] = useState(false)
  // The note about the venue's facts, closed with its X for this visit.
  const [factsNoticeClosed, setFactsNoticeClosed] = useState(false)
  const [pendingRebuild, setPendingRebuild] = useState<{
    settings: SiteSettings
    theme: Theme
    publishAfter: boolean
  } | null>(null)

  // One per panel: the overlay bar is measured off the element it drives.
  const formScrollRef = useRef<HTMLDivElement>(null)
  const propertiesScrollRef = useRef<HTMLDivElement>(null)

  const draft = useWebsiteStore(s => s.draft)
  const language = useWebsiteStore(s => s.editingLanguage)
  const setEditingLanguage = useWebsiteStore(s => s.setEditingLanguage)
  const publishErrors = useWebsiteStore(s => s.publishErrors)
  const publish = useWebsiteStore(s => s.publish)
  const undo = useWebsiteStore(s => s.undo)
  const redo = useWebsiteStore(s => s.redo)
  const canUndo = useWebsiteStore(s => s.past.length > 0)
  const canRedo = useWebsiteStore(s => s.future.length > 0)
  const customized = useWebsiteStore(s => s.customized)
  const buildFromTemplate = useWebsiteStore(s => s.buildFromTemplate)
  const selection = useWebsiteStore(s => s.selection)
  /*
    Any dialog over this page, from either source: the editor's own, and the
    shared crop dialog that a photo tile opens. Both are covered by a backdrop
    the overlay scrollbars would otherwise paint a bright line straight down,
    because a bar is z-index 30 and this app's modals sit at 5 and 6.
  */
  const dialogOpen = useDialogOpen()

  // The settings panel, opened and closed from the header's cog exactly as the
  // menu sidebar and the floor planner's editor are.
  const isPanelOpen = useWebsiteSettingsStore(s => s.isOpen)
  const setPanelOpen = useWebsiteSettingsStore(s => s.setIsOpen)

  // Open on entry, so nobody has to find the toggle to reach the page colours.
  useEffect(() => {
    if (mode === 'builder') setPanelOpen(true)
  }, [mode, setPanelOpen])

  /*
    Clicking a row or an element has to show its settings even when the panel
    was closed, or the click reads as having done nothing. Same rule as the
    floor planner, where selecting a table slides its editor open.
  */
  useEffect(() => {
    if (mode === 'builder' && selection) setPanelOpen(true)
  }, [mode, selection, setPanelOpen])

  useWebsiteQuery()
  useAutosave()
  useEditorShortcuts()
  /*
    The venue's menu, for the page's menu section. Read through the RMS here;
    the prerender will read the same lists through the public endpoints.
  */
  const { menu } = useRestaurantMenu()
  /*
    Photos this browser is holding, turned into object URLs the renderer can
    resolve. Without it a reload draws every picked photo as the placeholder.
  */
  useLocalAssetUrls()

  const restaurantSettings = useRestaurantSettingsStore(s => s.settings)
  // Undefined until it has been read, which leaves the document's copy alone.
  const googleLocation = useGoogleLocationQuery().data

  /*
    Every map on the page shows the venue's place as it is connected now, so
    connecting one in Settings fills the maps already placed, and
    disconnecting empties them. On every draft too, because an undo can bring
    back a map from before the last change.
  */
  const syncRestaurantMap = useWebsiteStore(s => s.syncRestaurantMap)
  useEffect(() => {
    if (googleLocation !== undefined) syncRestaurantMap(restaurantMapUrl(googleLocation))
  }, [googleLocation, draft, syncRestaurantMap])

  /*
    Whatever shape the document kept them in: a draft from before the sections
    existed is lifted here, so the form never renders one without them, and a
    document with no settings at all opens a blank form.
  */
  /*
    The venue's facts come from the restaurant details panel, which owns them;
    the document keeps a snapshot, and `toSiteSettings` is the one place the two
    shapes meet. Merged OVER the document so everything that belongs to the
    website — the template, the sections, the photos, the copied rooms and any
    translation typed here — survives.
  */
  const initialSettings = normalizeSiteSettings(
    toSiteSettings(restaurantSettings, draft.venue, googleLocation),
    draft.defaultLanguage
  )

  /*
    A published page is a snapshot, so it can fall behind the facts it was taken
    from. Saying so is the price of not rewriting a published page behind the
    owner's back, which is the alternative and is worse.
  */
  const snapshotIsStale = siteSnapshotIsStale(
    restaurantSettings,
    draft.venue,
    googleLocation
  )

  /*
    The form owns what is being typed; this is a mirror of it for the preview.
    Seeded from the document and refreshed by the form's onChange, so the page on
    the right is always the page the form would produce right now, saved or not.
  */
  const [previewSettings, setPreviewSettings] =
    useState<SiteSettings>(initialSettings)
  const [previewTheme, setPreviewTheme] = useState<Theme>(draft.theme)

  /*
    The canvas and the preview draw the page with the same custom properties the
    published page uses, so the faces have to be loaded here too. Both the saved
    pair and the one the form is currently showing: on the template tab the
    preview runs ahead of the document.
  */
  useGoogleFonts([
    ...Object.values(resolveFonts(draft.theme)),
    ...Object.values(resolveFonts(previewTheme))
  ])

  const handleFormChange = useCallback(
    (settings: SiteSettings, theme: Theme) => {
      setPreviewSettings(settings)
      setPreviewTheme(theme)
    },
    []
  )

  /*
    Always the generator's output, and only ever the template's own. The theme
    comes from `templateTheme` alone, with the chosen template's base under the
    form's two answers, so nothing the builder has done to this document
    reaches the page the form produces.

    Only the languages are read off the document, because those belong to the
    site rather than to the form.
  */
  const generate = useCallback(
    (settings: SiteSettings, theme: Theme) =>
      buildDocument(
        settings,
        draft.languages,
        draft.defaultLanguage,
        templateTheme(theme, templateOf(settings).base),
        { menu }
      ),
    [draft.languages, draft.defaultLanguage, menu]
  )

  /*
    A click on a section of the preview brings its card into view. No ring
    on the card afterwards: any click on the preview, a date picked in the
    reservations, outlined a card on the left, and it read as a stray focus
    ring.
  */
  const handleSectionClick = useCallback((rowId: string) => {
    const key = sectionOfRowId(rowId)
    const extra = extraOfRowId(rowId)
    let id: string | null = null
    if (key) id = sectionCardId(key)
    else if (extra) id = extraCardId(extra)
    if (!id) return
    document
      .getElementById(id)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [])

  /*
    Every text field in the form, not just the name. The template leaves out any
    section it has nothing to put in, so saving half a form produces a page
    quietly missing its address or its description.
  */
  const formComplete = siteSettingsComplete(
    previewSettings,
    language,
    draft.defaultLanguage
  )

  const previewDocument = useMemo(
    () => generate(previewSettings, previewTheme),
    [generate, previewSettings, previewTheme]
  )

  /*
    Saving from the template replaces the page. When the builder has touched
    it, that is destructive, so it asks first; the answer carries whether a
    publish was waiting behind it.
  */
  const saveFromTemplate = (
    settings: SiteSettings,
    theme: Theme,
    publishAfter = false
  ) => {
    // Refused with a reason rather than greyed out: a disabled button
    // could not say that the missing facts live in the settings, so the
    // note that does comes back if it was closed. The click is answered in
    // the banner as every refused save is; with the note already on screen
    // it would otherwise do nothing visible at all.
    if (!siteSettingsComplete(settings, language, draft.defaultLanguage)) {
      setFactsNoticeClosed(false)
      showSnackbar(
        t('ALERTS.SETTINGS_INVALID.TITLE'),
        t('WEBSITE.SETTINGS.FACTS_INCOMPLETE'),
        'error'
      )
      return
    }
    if (customized) {
      setPendingRebuild({ settings, theme, publishAfter })
      return
    }
    buildFromTemplate(generate(settings, theme))
    if (publishAfter) setPublishedNotice(publish())
  }

  const handlePublish = () => {
    // On the template tab the preview may be ahead of the draft. Publishing
    // what is on screen means saving it first; anything else is a trap.
    if (mode === 'template') {
      saveFromTemplate(previewSettings, previewTheme, true)
      return
    }
    setPublishedNotice(publish())
  }

  const handleConfirmRebuild = () => {
    if (pendingRebuild) {
      buildFromTemplate(generate(pendingRebuild.settings, pendingRebuild.theme))
      if (pendingRebuild.publishAfter) setPublishedNotice(publish())
    }
    setPendingRebuild(null)
  }

  const previewControls = (
    <div className={styles.toolbar} id="tour-website-toolbar">
      <div className={styles.toolbarGroup}>
        <IconButton
          icon="las la-desktop icon-big"
          color={device === 'desktop' ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.TOOLBAR.DESKTOP')}
          onClick={() => setDevice('desktop')}
        />
        <IconButton
          icon="las la-mobile icon-big"
          color={device === 'mobile' ? 'secondary' : 'primary'}
          ariaLabel={t('WEBSITE.TOOLBAR.MOBILE')}
          onClick={() => setDevice('mobile')}
        />
        {mode === 'builder' && (
          <>
            <IconButton
              icon="las la-undo icon-big"
              color="primary"
              disabled={!canUndo}
              ariaLabel={t('WEBSITE.TOOLBAR.UNDO')}
              onClick={undo}
            />
            <IconButton
              icon="las la-redo icon-big"
              color="primary"
              disabled={!canRedo}
              ariaLabel={t('WEBSITE.TOOLBAR.REDO')}
              onClick={redo}
            />
          </>
        )}
      </div>

      <div className={styles.toolbarGroup}>
        {/*
          Only when there is a choice to make. A venue with one language should
          not have to look at a dropdown holding one option.
        */}
        {draft.languages.length > 1 && (
          <div className={styles.languageSelect}>
            <TokenSelect
              label={t('WEBSITE.TOOLBAR.LANGUAGE')}
              value={language}
              options={draft.languages}
              translate={code => code.toUpperCase()}
              onChange={setEditingLanguage}
            />
          </div>
        )}
        {/*
          On the template tab Publish saves what is on screen before it
          publishes, so it refuses whatever Save refuses, with the same note.
        */}
        <Button
          label={t('WEBSITE.TOOLBAR.PUBLISH')}
          variant="contained"
          color="primary"
          onClick={handlePublish}
        />
      </div>
    </div>
  )

  /*
    Said before the owner presses Publish and is refused, not after. The rule
    itself is in `publishableDocumentSchema`, which is what actually stops it.
  */
  const localPhotos = hasLocalAssets(draft)

  /*
    The venue's facts, above the preview with the page's other notes rather
    than over Save: they are filled in on another page, and a box standing
    beside the button for the whole visit only repeated itself. Closable,
    since the form's own "Dane lokalu" card keeps saying what is missing.
  */
  const showFactsNotice = mode === 'template' && !factsNoticeClosed

  const notices = (
    <>
      {showFactsNotice && !formComplete && (
        <Alert
          severity="warning"
          message={t('WEBSITE.SETTINGS.FACTS_INCOMPLETE')}
          onClose={() => setFactsNoticeClosed(true)}
        />
      )}
      {showFactsNotice && formComplete && snapshotIsStale && (
        <Alert
          severity="info"
          message={t('WEBSITE.SETTINGS.FACTS_STALE')}
          onClose={() => setFactsNoticeClosed(true)}
        />
      )}
      {localPhotos && (
        <Alert severity="warning" message={t('WEBSITE.PUBLISH_LOCAL_PHOTOS')} />
      )}
      {publishedNotice && (
        <Alert
          severity="success"
          message={t('WEBSITE.PUBLISH_OK')}
          onClose={() => setPublishedNotice(false)}
        />
      )}
      {publishErrors.length > 0 && (
        <Alert
          severity="warning"
          message={
            <span>
              {t('WEBSITE.PUBLISH_BLOCKED')}
              <br />
              {publishErrors.slice(0, 3).join(' · ')}
            </span>
          }
        />
      )}
    </>
  )

  /*
    The modals live OUTSIDE <Grid>, and that is not a style preference. Grid
    wraps EVERY child in a `grid__item` and gives one with no col/start classes
    `start-1 col-1`, which walks the auto-placement cursor into the next row and
    drops whatever column comes after it below the fold.
  */
  if (mode === 'template') {
    return (
      <>
        <Grid>
          <div className={`${styles.builderColumn} start-2 col-5`}>
            <Tile color="black">
              <div className={styles.editorSection}>
                <p className={`title-bold-medium ${styles.title}`}>
                  {t('WEBSITE.TEMPLATE.TITLE')}
                </p>
                <p className={`body-medium ${styles.lead}`}>
                  {t('WEBSITE.TEMPLATE.LEAD')}
                </p>
                {/*
                  The app's overlay scrollbar, not a native one: a native bar
                  that widens on hover takes layout width and shoves the whole
                  form sideways under the pointer (CLAUDE.md §6).
                */}
                {/*
                  The scrolling column and, under it, the save. Two elements and
                  not one, because the button has to stay put while the form
                  moves: anything inside the scroller is masked with it.
                */}
                <div className={styles.formColumn}>
                  <div className={styles.panelHost}>
                    {!dialogOpen && (
                      <OverlayScrollbar targetRef={formScrollRef} />
                    )}
                    <div
                      className={`${styles.panelSection} ${styles.formSection}`}
                      id="tour-website-properties"
                      ref={formScrollRef}
                    >
                      <SiteSettingsForm
                        initial={initialSettings}
                        theme={draft.theme}
                        language={language}
                        onChange={handleFormChange}
                        hasMenu={Boolean(menu && menu.categories.length > 0)}
                      />
                    </div>
                  </div>
                  {/*
                    The page saves, not the form. It already held what the form
                    has typed, because the preview beside it is drawn from the
                    same two values, and Publish has always saved from them too.
                  */}
                  <div className={styles.formFooter}>
                    <Button
                      label={t('WEBSITE.SETTINGS.CREATE')}
                      variant="contained"
                      color="primary"
                      onClick={() =>
                        saveFromTemplate(previewSettings, previewTheme)
                      }
                    />
                  </div>
                </div>
              </div>
            </Tile>
          </div>

          <div className={`${styles.builderColumn} start-7 col-6`}>
            <Tile color="black">
              <div className={styles.editorSection}>
                {previewControls}
                {notices}
                <PagePreview
                  document={previewDocument}
                  language={language}
                  device={device}
                  hideScrollbar={dialogOpen}
                  onSectionClick={handleSectionClick}
                  menu={menu}
                />
              </div>
            </Tile>
          </div>
        </Grid>

        <ConfirmationModal
          isOpen={pendingRebuild !== null}
          title={t('WEBSITE.SETTINGS.REGENERATE_TITLE')}
          message={t('WEBSITE.SETTINGS.REGENERATE_MESSAGE')}
          onConfirm={handleConfirmRebuild}
          onClose={() => setPendingRebuild(null)}
        />
      </>
    )
  }

  /*
    The canvas takes the panel's width back when it closes. `grid-hidden` and
    not `hidden`: Grid understands the first and drops the child, while the
    second leaves an empty `grid__item` behind at `start-1 col-1`.
  */
  const canvasColumnClassName = isPanelOpen ? 'start-2 col-8' : 'start-2 col-11'
  const panelColumnClassName = isPanelOpen
    ? `${styles.builderColumn} start-10 col-3`
    : 'grid-hidden'

  return (
    <Grid>
      <div className={`${styles.builderColumn} ${canvasColumnClassName}`}>
        {/*
          Black, like every other page-level tile in this app and like the floor
          planner, which is the precedent CLAUDE.md §1 names for a canvas editor.
          Nested surfaces here go DARKER, never lighter: the well inside is darker
          again, and the only light thing on the screen is the document itself.
        */}
        <Tile color="black">
          <div className={styles.editorSection}>
            {previewControls}
            {notices}
            <EditorCanvas device={device} />
          </div>
        </Tile>
      </div>

      <div className={panelColumnClassName}>
        {/*
          The menu sidebar's padding, not the page tile's. This column is the
          same thing that column is, a stack of settings in a narrow tile, and
          at the default `2rem` sides its groups came out 60px narrower than
          the ones in menu management for no reason anybody could see.

          No padding on the right, because `.panelSection` already reserves
          0.75rem there for the overlay scrollbar to sit in. Adding the tile's
          own on top would inset the content twice on that side only.
        */}
        <Tile color="black" customPadding="1rem 0 0.5rem 0.5rem">
          {/*
            The heading is inside the flex column rather than the Tile's own
            `title` prop: the Card is a block, so a title above `.panelHost`
            leaves its `height: 100%` resolving against the full card and the
            scroll area ends up taller than the space left for it.
          */}
          <div className={styles.editorSection}>
            <p className={`title-bold-medium ${styles.title}`}>
              {t('WEBSITE.PANEL.TITLE')}
            </p>
            <div className={styles.panelHost}>
              {!dialogOpen && (
                <OverlayScrollbar targetRef={propertiesScrollRef} />
              )}
              <div
                className={styles.panelSection}
                id="tour-website-properties"
                ref={propertiesScrollRef}
              >
                <PropertiesPanel />
              </div>
            </div>
          </div>
        </Tile>
      </div>
    </Grid>
  )
}

export default WebsiteBuilder
